import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LanguagesData } from 'api/UtilsAPI'
import { IProfileWarningsResult, WarningSection } from './warnings/types'
import { chooseDBTranslation } from 'utils/translations'
import { Translation } from 'types/translation'
import { ISkillTranslation } from 'types/levelItemInterfaces'
import {
  ICertificateTranslation,
  ICompetenceTranslation,
  ICourseTranslation,
  IEducationTranslation,
  IEmployerTranslation,
  IProjectTranslation,
} from 'types/cvsInterfaces'

interface ISectionItemWarningsProps {
  section: WarningSection
  sectionTitleKey: string
  warnings: IProfileWarningsResult
  maxItems?: number
  languages?: LanguagesData
}

const SectionItemWarnings: React.FC<ISectionItemWarningsProps> = ({
  section,
  sectionTitleKey,
  warnings,
  maxItems = 5,
  languages,
}) => {
  const { t, i18n } = useTranslation()
  const items = warnings.itemsBySection[section]

  if (!items || items.length === 0) {
    return null
  }

  const visibleItems = items.slice(0, maxItems)
  const hiddenItemsCount = items.length - visibleItems.length

  const getItemName = (translations: Translation[], section: WarningSection) => {
    if (translations) {
      // Return name based on section type
      switch (section) {
        case 'skills':
        case 'industries':
        case 'roles':
          return chooseDBTranslation(i18n, translations as unknown as ISkillTranslation[]).name

        case 'employers':
          return chooseDBTranslation(i18n, translations as unknown as IEmployerTranslation[]).name

        case 'projects':
          return chooseDBTranslation(i18n, translations as unknown as IProjectTranslation[]).name

        case 'education':
          return chooseDBTranslation(i18n, translations as unknown as IEducationTranslation[]).school

        case 'courses':
          return chooseDBTranslation(i18n, translations as unknown as ICourseTranslation[]).course

        case 'certificates':
          return chooseDBTranslation(i18n, translations as unknown as ICertificateTranslation[]).certificate

        case 'competences':
          return chooseDBTranslation(i18n, translations as unknown as ICompetenceTranslation[]).name

        default:
          return ''
      }
    }

    return ''
  }

  const getWarningItemId = (section: WarningSection, itemId: number | string) => `profile-item-${section}-${itemId}`

  const scrollToItem = (section: WarningSection, itemId: number | string) => {
    window.dispatchEvent(
      new CustomEvent('profile-warning-item-click', {
        detail: { section, itemId },
      })
    )

    const target = document.getElementById(getWarningItemId(section, itemId))
    if (target) {
      target.classList.remove('profile-warning-item-highlight')
      void target.offsetWidth
      target.classList.add('profile-warning-item-highlight')
    }
    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
    target?.focus({ preventScroll: true })
  }

  return (
    <Alert
      severity="warning"
      data-testid={`profile-section-warning-${section}`}
      variant="filled"
      sx={{
        mb: 1,
        color: '#333333',
      }}
    >
      <Typography variant="body2" fontWeight="bold" mb={0.5} color="inherit">
        {t('profile.warnings.sectionDetailsTitle', {
          section: t(sectionTitleKey),
          count: items.length,
        })}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {visibleItems.map(item => {
          const fields = item.missingFields
            .map(fieldName => t(`profile.warnings.field.${fieldName}`, { defaultValue: fieldName }))
            .join(', ')
          const name =
            section === 'languages'
              ? languages?.[item.languageCode ?? ''] || item.languageCode || item.itemId
              : item.translations
                ? getItemName(item.translations, section)
                : item.itemId

          return (
            <Typography
              key={`${section}-${item.itemId}-${item.itemIndex}`}
              component="li"
              variant="body2"
              color="inherit"
            >
              <Link
                component="button"
                type="button"
                onClick={() => scrollToItem(section, item.itemId)}
                color="inherit"
                underline="always"
                sx={{ textAlign: 'left', font: 'inherit' }}
              >
                {t('profile.warnings.itemMissing', {
                  index: item.itemIndex + 1,
                  name,
                  fields,
                })}
              </Link>
            </Typography>
          )
        })}
      </Box>
      {hiddenItemsCount > 0 && (
        <Typography variant="body2" mt={0.5} color="inherit">
          {t('profile.warnings.moreItems', { count: hiddenItemsCount })}
        </Typography>
      )}
    </Alert>
  )
}

export default SectionItemWarnings
